import React from 'react'
import {
  Box,
  Button,
  InputBase,
  SxProps,
  styled,
  Typography,
  useTheme
} from '@mui/material'

import { Icon } from './Icon'

type FormProps = {
  formProps: {
    form_id: string
    customFormId: string
    section_id?: string
    ref_form_input: {
      fields: {
        additional_validation: string
        field_label: string
        field_name: string
        field_placeholder: string
        field_type: string
        is_required: string
        show_label: string
      }[]
      submit_button_text: string
      title: string
      tracking_field_name: string
    }
    additional_track_calls_new: {
      analytic_sdk_name: string
      analytic_function_name: string
      function_arguments: { argument_type: string; argument_value: string }[]
    }[]
    usebasin_endpoint: string
    on_success_message: string
    on_success_navigate_url?: string
  }
  errorAfterButton?: boolean
  formData: {
    [key: string]: string | boolean
  }
  formError: {
    [key: string]: string | boolean
  }
  formSubmitted: boolean
  handleChange: (
    field: string
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void
  handleSubmit: (e: React.SyntheticEvent) => Promise<void>
  sx?: SxProps
}

const Input = styled(InputBase)`
  ${({ theme }) => `
    width: 100%;
    
    input, textarea {
      background-color: ${theme.palette.background.default};
      border: 1px solid ${theme.palette.border.main};
      border-radius: ${theme.spacing(1)};
      padding: ${theme.spacing(1.5)} ${theme.spacing(2)};

      ::placeholder {
        color: ${theme.palette.text.secondary};
        opacity: 1;
      }
    }
  `}
`

export const DynamicInputFormSingleField = ({
  formProps,
  errorAfterButton,
  formData,
  formError,
  formSubmitted,
  handleChange,
  handleSubmit,
  sx
}: FormProps) => {
  const { palette } = useTheme()
  const formDefinition = formProps.ref_form_input
  const formField = formDefinition.fields[0]

  return (
    <Box>
      <Box
        component='form'
        id={formProps.form_id}
        data-test='single-field-form'
        noValidate
        onSubmit={handleSubmit}
        sx={{
          /* Can be removed after tailwind is removed */
          button: {
            background:
              'linear-gradient(135.55deg, #3E35D1 6.09%, #86F8E0 116.03%)'
          },
          ...sx
        }}>
        <Input
          type={formField.additional_validation}
          name={formField.field_name}
          placeholder={formField.field_placeholder}
          id={formField.field_name}
          aria-required='true'
          aria-invalid='false'
          value={formData[formField.field_name]}
          onChange={handleChange(formField.field_name)}
          sx={{
            input: {
              pl: 1,
              fontSize: { xs: 14, laptop: 16 },

              /* Can be removed after tailwind is removed */
              ':-webkit-autofill': {
                caretColor: '#070E1F',
                WebkitBoxShadow: '0 0 0 25px #fff inset',
                WebkitTextFillColor: '#070E1F'
              }
            }
          }}
        />

        {!errorAfterButton && formError[formField.field_name] && (
          <Typography
            variant='subtitle2'
            data-test='error-message'
            sx={{ color: 'error.main', mt: 0.5, px: 2 }}
          >
            The email address entered is invalid.
          </Typography>
        )}

        <Button
          variant='contained'
          type='submit'
          sx={{
            alignItems: 'center',
            width: '100%',
            borderRadius: 2,
            color: 'background.default',
            fontSize: { xs: 14, laptop: 16 },
            fontWeight: 700,
            mt: { xs: 2, laptop: 3 },
            py: 1.25,
            '&.Mui-disabled': {
              color: 'background.default'
            },
            ...(formSubmitted && {
              pointerEvents: 'none',
              background: `${palette.success.main} !important`
            })
          }}
          startIcon={
            formSubmitted && (
              <Icon
                icon='check-circle'
                color='#fff'
                fontSize='16px'
                sx={{ mt: '3px' }}
              />
            )
          }>
          {formSubmitted
            ? formProps.on_success_message || 'Submitted'
            : formDefinition.submit_button_text}
        </Button>
      </Box>

      {errorAfterButton && formError[formField.field_name] && (
        <Typography
          variant='subtitle2'
          data-test='error-message'
          sx={{
            color: 'error.main',
            mt: 1.5,
            px: 2,
            textAlign: { xs: 'center', mobile: 'left' }
          }}>
          The email address entered is invalid.
        </Typography>
      )}
    </Box>
  )
}
